<script>
    import { onMount } from "svelte";
    import { fly } from "svelte/transition";
    import { cubicInOut } from "svelte/easing";
    import { buildTextFromValues, getMonthLength, toText } from "./date-utils";
    import { parse, createFormat } from "./parse.js";
    import DateTimePicker from "./DatePicker.svelte";
    import { writable } from "svelte/store";
    import { createEventDispatcher } from "svelte";
    import { v4 } from "uuid";

    export { classes as class };
    export let text = toText($store, formatTokens);
    export let value = null,
        showYearDropdown = true,
        showTime = false,
        id = UID,
        placeholder = "2023-12-31 23:00:00",
        valid = true,
        disabled = false,
        required = false,
        format = "dd/MM/yyyy HH:mm:ss",
        locale = {},
        min = new Date(defaultDate.getFullYear() - 20, 0, 1),
        max = new Date(defaultDate.getFullYear(), 11, 31, 23, 59, 59, 999),
        visible = false,
        showAbove = false,
        showRight = false,
        wrapperClass,
        wrapperClassFocus,
        closeOnSelection = false,
        browseWithoutSelecting = false,
        showClearButton = false,
        inputClass = "pr-2 py-1",
        timePrecision = null;

    export let minYear,
        maxYear,
        showScrollYearOptions = false,
        yearOptionSize;

    onMount(() => {
      if (/iP(hone|od|ad)/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent)) {
        isIos = true;
      }
    })

    let classes = "pl-3";
    const dispatch = createEventDispatcher();
    const UID = v4();
    const defaultDate = /* @__PURE__ */ new Date();
    const innerStore = writable(null);
    const values = {
        yyyy: "",
        MM: "",
        dd: "",
        HH: "",
        mm: "",
        ss: "",
    };
    const store = (() => {
        return {
            subscribe: innerStore.subscribe,
            set: (date) => {
                if (date === null || date === void 0) {
                    innerStore.set(null);
                    if (value) {
                        value = null;
                        dispatch("change");
                    }
                } else if (date.getTime() !== $innerStore?.getTime()) {
                    innerStore.set(date);
                    if (value?.getTime() !== date.getTime()) {
                        value = date;
                        dispatch("change");
                    }
                }
            },
        };
    })();

    let previousKeyDown, isIos;

    $: store.set(value);
    $: formatTokens = createFormat(format);
    $: valueToText($store, formatTokens);
    function valueToText(value2, formatTokens2) {
        text = toText(value2, formatTokens2);
    }

    $: textToValue(text, formatTokens);
    function textToValue(text2, formatTokens2) {
        if (text2.length) {
            const result = parse(text2, formatTokens2, $store);
            if (result.valid) {
                valid = true;
                store.set(result.date);
                values.yyyy = result.year;
                values.MM = result.month?.toString().padStart(2, "0");
                values.dd = result.day?.toString().padStart(2, "0");
                values.HH = result.hours?.toString().padStart(2, "0");
                values.mm = result.minutes?.toString().padStart(2, "0");
                values.ss = result.seconds?.toString().padStart(2, "0");
            } else {
                valid = false;
            }
        } else {
            valid = true;
            if (value) {
                value = null;
                store.set(null);
            }
        }
    }
    function onFocusOut(e) {
        if (
            e?.currentTarget instanceof HTMLElement &&
            e.relatedTarget &&
            e.relatedTarget instanceof Node &&
            e.currentTarget.contains(e.relatedTarget)
        ) {
            return;
        } else {
            if(!isIos)
                visible = false;
        }
    }
    function keydown(e) {
        if (e.key === "Escape" && visible) {
            visible = false;
            e.preventDefault();
            e.stopPropagation();
        } else if (e.key === "Enter") {
            visible = !visible;
            e.preventDefault();
        }
    }
    function onSelect(e) {
        dispatch("change", e.detail);
        if (closeOnSelection) {
            visible = false;
        }
    }
    export let dynamicPositioning = false;
    export let containerElement;
    let InputElement;
    let pickerElement;
    let pickerLeftPosition = null;

    function setDatePickerPosition() {
        pickerLeftPosition = null;
        if (visible && pickerElement && dynamicPositioning) {
            const inputRect = InputElement.getBoundingClientRect();
            const pickerWidth = pickerElement.offsetWidth;
            const pickerHeight = pickerElement.offsetHeight;

            // Get container boundary
            const containerRect = containerElement.getBoundingClientRect();
            const containerRight = containerRect.right;

            const horizontalOverflow = pickerWidth - inputRect.width;
            const bottomThreshold = inputRect.bottom + pickerHeight + 5;
            const rightThreshold = inputRect.left + pickerWidth + 5;

            if (bottomThreshold > window.innerHeight) showAbove = true;

            if (showRight) {
                // When showRight is true, align picker to the left side of the input
                pickerLeftPosition = inputRect.left - pickerWidth;

                // Ensure picker doesn't overflow beyond the container's right boundary
                // if (pickerLeftPosition + pickerWidth > containerRight) {
                //     pickerLeftPosition = containerRight - pickerWidth;
                // }
            } else {
                // Original logic for positioning picker
                if (rightThreshold > containerRight) {
                    pickerLeftPosition = -horizontalOverflow;

                    if (inputRect.left < horizontalOverflow + 5) {
                        // Align picker to the center
                        const windowCenterPos = window.innerWidth / 2;
                        const newPos = windowCenterPos - pickerWidth / 2;
                        pickerLeftPosition = newPos - inputRect.left;

                        // Ensure picker is not out of container after adjustment
                        if (
                            inputRect.left + pickerLeftPosition <
                            containerRect.left
                        )
                            pickerLeftPosition =
                                containerRect.left - inputRect.left;
                        else if (
                            inputRect.left + pickerLeftPosition + pickerWidth >
                            containerRight
                        )
                            pickerLeftPosition =
                                containerRight - inputRect.left - pickerWidth;
                    }
                }
            }
        }
    }

    function flyAutoPosition(node) {
        setDatePickerPosition();
        return fly(node, {
            duration: 200,
            easing: cubicInOut,
            y: showAbove ? 5 : -5,
        });
    }

    const moveFocus = (current) => {
        const focussable = Array.prototype.filter.call(
            document.querySelectorAll("input:not([disabled])"),
            function (element) {
                //check for visibility while always include the current activeElement
                return (
                    element.offsetWidth > 0 ||
                    element.offsetHeight > 0 ||
                    element === document.activeElement
                );
            },
        );

        if (focussable.indexOf(current) > -1) {
            if (focussable.indexOf(current) === focussable.length - 1)
                current.blur();
            else focussable[focussable.indexOf(current) + 1]?.focus();
        }
    };

    const onInputKeyDown = (e, keyID) => {
        const { key } = e;
        // filter invalid key press
        if (key.length === 1 && !key.match(/^[0-9]+$/) && !e.ctrlKey)
            e.preventDefault();
        // handle up/down key press
        else if (key === "ArrowUp" || key === "ArrowDown") {
            let inputValue = e.target.value;
            switch (keyID) {
                case "yyyy":
                    if (inputValue && parseInt(inputValue)) {
                        if (
                            Number(inputValue) < max.getFullYear() &&
                            key === "ArrowUp"
                        )
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (
                            Number(inputValue) > min.getFullYear() &&
                            key === "ArrowDown"
                        )
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "1900";
                    }
                    break;
                case "MM":
                    if (inputValue && parseInt(inputValue)) {
                        if (Number(inputValue) < 12 && key === "ArrowUp")
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (Number(inputValue) > 1 && key === "ArrowDown")
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "01";
                    }
                    if (inputValue && inputValue.length === 1) {
                        inputValue = inputValue.padStart(2, "0");
                    }
                    break;
                case "dd":
                    if (inputValue && parseInt(inputValue)) {
                        const maxMonthLength = getMonthLength(
                            values.yyyy ? values.yyyy : 1,
                            values.MM ? values.MM - 1 : 0,
                        );
                        if (
                            Number(inputValue) < maxMonthLength &&
                            key === "ArrowUp"
                        )
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (Number(inputValue) > 1 && key === "ArrowDown")
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "01";
                    }
                    if (inputValue && inputValue.length === 1)
                        inputValue = inputValue.padStart(2, "0");
                    break;
                case "HH":
                    if (inputValue && parseInt(inputValue)) {
                        if (Number(inputValue) < 23 && key === "ArrowUp")
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (Number(inputValue) > 0 && key === "ArrowDown")
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "01";
                    }
                    if (inputValue && inputValue.length === 1) {
                        inputValue = inputValue.padStart(2, "0");
                    }
                    break;
                case "mm":
                    if (inputValue && parseInt(inputValue)) {
                        if (Number(inputValue) < 59 && key === "ArrowUp")
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (Number(inputValue) > 0 && key === "ArrowDown")
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "01";
                    }
                    if (inputValue && inputValue.length === 1) {
                        inputValue = inputValue.padStart(2, "0");
                    }
                    break;
                case "ss":
                    if (inputValue && parseInt(inputValue)) {
                        if (Number(inputValue) < 59 && key === "ArrowUp")
                            inputValue = (parseInt(inputValue) + 1).toString();
                        if (Number(inputValue) > 0 && key === "ArrowDown")
                            inputValue = (parseInt(inputValue) - 1).toString();
                    } else {
                        inputValue = "01";
                    }
                    if (inputValue && inputValue.length === 1) {
                        inputValue = inputValue.padStart(2, "0");
                    }
                    break;
                default:
                    break;
            }
            e.target.value = inputValue;
            values[keyID] = inputValue;
            e.preventDefault();
        } else if (
            // handle overwrite press
            key.match(/^[0-9]+$/) &&
            e.target.value.length === e.target.maxLength &&
            e.target.selectionEnd === e.target.selectionStart &&
            e.target.maxLength > e.target.selectionStart
        ) {
            e.preventDefault();
            const { selectionStart } = e.target;
            e.target.value =
                e.target.value.substring(0, selectionStart) +
                key +
                e.target.value.substring(selectionStart + 1);
            e.target.selectionEnd = selectionStart + 1;
            e.target.selectionStart = selectionStart + 1;
            if (selectionStart + 1 === e.target.maxLength) {
                moveFocus(e.target);
            }
        }
    };

    const moveToNextInput = (e) => {
        if (
            e.target.value.length === e.target.maxLength &&
            e.target.selectionEnd === e.target.maxLength
        ) {
            moveFocus(e.target);
        }
    };

    const updateValue = (e, key) => {
        let inputValue = e.target.value;

        switch (key) {
            case "yyyy":
                if (inputValue && parseInt(inputValue)) {
                    if (Number(inputValue) > max.getFullYear())
                        inputValue = max.getFullYear();
                    else if (Number(inputValue) < min.getFullYear())
                        inputValue = min.getFullYear();
                } else inputValue = min.getFullYear();
                break;
            case "MM":
                if (inputValue && parseInt(inputValue)) {
                    if (Number(inputValue) > 12) inputValue = 12;
                    else if (Number(inputValue) < 1) inputValue = 1;
                }
                if (inputValue && inputValue.length === 1)
                    inputValue = inputValue.padStart(2, "0");
                break;
            case "dd":
                if (inputValue && parseInt(inputValue)) {
                    const maxMonthLength = getMonthLength(
                        values.yyyy ? values.yyyy : 1,
                        values.MM ? values.MM - 1 : 0,
                    );
                    if (Number(inputValue) > maxMonthLength)
                        inputValue = maxMonthLength;
                    else if (Number(inputValue) < 1) inputValue = 1;
                }
                if (inputValue && inputValue.length === 1)
                    inputValue = inputValue.padStart(2, "0");
                break;
            case "HH":
                if (inputValue && parseInt(inputValue)) {
                    if (Number(inputValue) > 23) inputValue = 23;
                    else if (Number(inputValue) < 0) inputValue = 0;
                }
                if (inputValue && inputValue.length === 1)
                    inputValue = inputValue.padStart(2, "0");
                break;
            case "mm":
                if (inputValue && parseInt(inputValue)) {
                    if (Number(inputValue) > 59) inputValue = 59;
                    else if (Number(inputValue) < 0) inputValue = 0;
                }
                if (inputValue && inputValue.length === 1)
                    inputValue = inputValue.padStart(2, "0");
                break;
            case "ss":
                if (inputValue && parseInt(inputValue)) {
                    if (Number(inputValue) > 59) inputValue = 59;
                    else if (Number(inputValue) < 0) inputValue = 0;
                }
                if (inputValue && inputValue.length === 1)
                    inputValue = inputValue.padStart(2, "0");
                break;
            default:
                break;
        }

        e.target.value = inputValue;
        values[key] = inputValue;
        text = buildTextFromValues(values, formatTokens);
    };

    const showPicker = () => {
        if (visible || disabled) return;
        visible = true;
        onPlaceholderClick();
    };

    const clearValue = () => {
        visible = false;
        value = null;
        Object.keys(values).forEach((key) => (values[key] = ""));

        dispatch("change");
    };

    const onPlaceholderClick = () => {
        if (disabled) return;
        // wait for inputs render
        setTimeout(() => {
            if (!UID) return;
            const allInputs = document.getElementsByClassName(UID);
            if (allInputs && allInputs.length) {
                allInputs[0]?.focus();
            }
        }, 100);
    };

    $: if (visible) {
        window.addEventListener(
            "click",
            function eventHandler(e) {
                const dropdownSelect = document.getElementById(id);
                if (
                    dropdownSelect &&
                    !document.getElementById(id)?.contains(e.target)
                ) {
                    visible = false;
                    window.removeEventListener("click", eventHandler, true);
                }
            },
            true,
        );

        window.addEventListener(
            "focus",
            function focusHandler(e) {
                const dropdownSelect = document.getElementById(id);
                if (
                    dropdownSelect &&
                    !document.getElementById(id)?.contains(e.target)
                ) {
                    visible = false;
                    window.removeEventListener("focus", focusHandler, true);
                }
            },
            true,
        );

        if (document.onkeydown) previousKeyDown = document.onkeydown;

        document.onkeydown = (event) => {
            if (event.key === "Escape") {
                visible = false;
                document.onkeydown = previousKeyDown ?? null;
            }
        };
    }

    export const refreshValue = () => {
        visible = true;
        setTimeout(() => {
            visible = false;
        }, 50);
    };
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="date-time-field w-full {disabled
        ? 'bg-gray-200'
        : ''} {classes} {wrapperClass} {visible ? wrapperClassFocus : ''}"
    on:focusout={onFocusOut}
    on:keydown={keydown}
    class:invalid={!valid && !showClearButton}
    {id}
    bind:this={InputElement}
>
    <span
        class="relative flex flex-row gap-x-0 flex-1 items-center {inputClass} {disabled
            ? 'cursor-not-allowed'
            : ''}"
        on:click={showPicker}
        on:focus={showPicker}
        tabindex="0"
    >
        {#if placeholder && !visible && !value}
            <span
                class="opacity-60 w-full {disabled
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'}"
                on:click={onPlaceholderClick}
            >
                {placeholder}
            </span>
        {:else}
            {#each formatTokens as token, index}
                {#if typeof token == "string"}
                    {#if showYearDropdown || index < 3}
                        <span>{token}</span>
                    {/if}
                {:else if showYearDropdown || index < 3}
                    <input
                        value={value ? token.toString(value) : ""}
                        type="text"
                        class="{UID} text-primary {disabled
                            ? 'cursor-not-allowed'
                            : ''} {token.id.length === 2
                            ? 'w-[23px]'
                            : 'w-[43px]'}"
                        maxlength={token.id.length}
                        on:blur={(e) => updateValue(e, token.id)}
                        on:keydown={(e) => onInputKeyDown(e, token.id)}
                        on:input={moveToNextInput}
                        on:focus={showPicker}
                        {disabled}
                    />
                {/if}
            {/each}
            {#if showClearButton && value}
                <button
                    class="absolute right-4 pt-1"
                    on:click|stopPropagation={clearValue}
                >
                    <i
                        class="fa fa-times"
                        style="color: #6F726C;"
                        aria-hidden="true"
                    ></i>
                </button>
            {/if}
        {/if}
    </span>
    {#if visible && !disabled}
        <div
            class="picker"
            class:visible
            class:above={showAbove}
            transition:flyAutoPosition
            bind:this={pickerElement}
            style:--picker-left-position="{pickerLeftPosition}px"
        >
            <DateTimePicker
                on:focusout={onFocusOut}
                on:select={onSelect}
                bind:value={$store}
                {min}
                {max}
                {locale}
                {browseWithoutSelecting}
                {showYearDropdown}
                {minYear}
                {maxYear}
                {showScrollYearOptions}
                {yearOptionSize}
            />
        </div>
    {/if}
</div>

<style>
    .date-time-field {
        position: relative;
        display: flex;
        flex-direction: row;
        gap: 0;
    }

    input[type="text"] {
        margin: 0;
        padding: 0 2px;
        height: 18px;
        line-height: 18px;
        background: transparent;
    }

    input[type="text"]:focus {
        outline: 0;
    }

    .invalid {
        border: 1px solid rgba(249, 47, 114, 0.5);
        background-color: rgba(249, 47, 114, 0.1);
        border-radius: 5px;
    }
    .invalid:focus {
        border-color: #f92f72;
        box-shadow: 0px 0px 0px 2px rgba(249, 47, 114, 0.5);
    }

    .picker {
        display: none;
        position: absolute;
        padding: 1px;
        left: var(--picker-left-position);
        z-index: 100;
        top: 100%;
    }
    .picker.above {
        bottom: 100%;
        top: auto !important;
    }
    .picker.visible {
        display: block;
    }
</style>
